import { useSyncExternalStore } from 'react';

/**
 * Store du token d'accès côté client.
 *
 * Le token vit **en mémoire** (variable de module) — jamais dans
 * localStorage/sessionStorage, qui l'exposeraient à l'exfiltration XSS et
 * annuleraient l'intérêt du refresh token en cookie httpOnly (T-05). Au
 * reload, il est perdu puis ré-hydraté par le silent refresh du boot (G-05).
 *
 * Deux surfaces : impérative (client GraphQL, flux de refresh) et réactive
 * (`useAccessToken`). Ce store ne connaît QUE le token — l'utilisateur
 * viendra de la query `me` dans TanStack Query, pas d'état dupliqué ici.
 */

let accessToken: string | null = null;
const listeners = new Set<() => void>();

function setToken(next: string | null): void {
  if (next === accessToken) return;
  accessToken = next;
  for (const listener of listeners) listener();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string): void {
  setToken(token);
}

export function clearAccessToken(): void {
  setToken(null);
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// La coquille prérendue par Start ne porte jamais de token : null côté
// serveur, cohérent avec la première passe d'hydratation.
function getServerSnapshot(): string | null {
  return null;
}

export function useAccessToken(): string | null {
  return useSyncExternalStore(subscribe, getAccessToken, getServerSnapshot);
}
